import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import keycloak from '../keycloak/keycloak';
import { logUserOut } from '../user/userActions';
import { clearCDNCachedContent } from '../utils/offlineUtils';
import { dataDogAddWarning } from '../logging/datadog';

// Persistent container that survives React mount/unmount cycles.
// amp-navigation-v2 v2 uses createRoot() internally, which can only be called
// once per DOM container. By reusing the same container, we avoid the crash
// when the component remounts (e.g. after completing an assessment).
let persistentAmpNavContainer = null;

const getOrCreatePersistentContainer = () => {
    if (!persistentAmpNavContainer) {
        persistentAmpNavContainer = document.createElement('nav');
        persistentAmpNavContainer.id = 'amp-nav-v2-container';
    }
    return persistentAmpNavContainer;
};

const AmpNavigationContainer = ({ trackLogOut }) => {
    const placeholderRef = useRef(null);

    // Attach/detach the persistent container to/from the placeholder
    useEffect(() => {
        const container = getOrCreatePersistentContainer();
        const placeholder = placeholderRef.current;

        if (placeholder) {
            container.style.display = '';
            placeholder.appendChild(container);
        }

        // Reset hideNavigation on remount — StartView sets it to true when
        // an assessment begins, and the persistent container preserves that
        // state across unmount/remount cycles.
        if (window.AmpNavigationV2Module?.updateConfig) {
            window.AmpNavigationV2Module.updateConfig({ hideNavigation: false });
        }

        return () => {
            // Move container to body (hidden) so it stays alive in the DOM
            container.style.display = 'none';
            document.body.appendChild(container);
        };
    }, []);

    // Initialize renderAmpNavV2 only once
    useEffect(() => {
        const container = getOrCreatePersistentContainer();

        // Guard: only call renderAmpNavV2 if not already rendered
        if (container.hasChildNodes()) {
            return;
        }

        try {
            window.AmpNavigationV2Module.renderAmpNavV2('amp-nav-v2-container', {
                _keycloak: keycloak,
                pageTitle: 'mCLASS',
                customLogoutHandler: () => {
                    trackLogOut()
                        .then(clearCDNCachedContent())
                        .then(logUserOut());
                },
            });
        } catch (error) {
            dataDogAddWarning('Failed to load AmpNavigationV2Module', { error });
            // eslint-disable-next-line no-console
            console.warn('Failed to load AmpNavigationV2Module:', error);
        }
    }, []);

    return <div ref={placeholderRef} />;
};

AmpNavigationContainer.propTypes = {
    trackLogOut: PropTypes.func,
};

AmpNavigationContainer.defaultProps = {
    trackLogOut: () => {},
};

// Exported for testing only
export const __resetPersistentContainer = () => {
    if (persistentAmpNavContainer && persistentAmpNavContainer.parentNode) {
        persistentAmpNavContainer.parentNode.removeChild(persistentAmpNavContainer);
    }
    persistentAmpNavContainer = null;
};

export default AmpNavigationContainer;
